import React, { useState } from 'react';
import { PageTitle } from '../../layout/core';
import '../settlements/SettlementReconcile.scss';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import {
	fetchBatchDetailsData,
	IBatchDetailsData,
	IBatchDetailsDataSearchParams,
} from '../../store/order/actions/batches';
import { exportCSVBatchDetailsData } from '../../store/order/actions/exports';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../table/Table';
import { BatchDetailsInformation } from './BatchDetailsInformation';
import { OrderStatus } from '../orders/OrderStatus';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { usePaymentContext } from '../../store/payments/payments-context';
import { fundsBreadCrumbs } from '../../views/funds-reserve/FundsReservePage';
import { ROUTES } from '../../routing/routesEnums';
import { IBatchesData } from '../../interfaces/funds';
import { BATCHES_BASE_URL, BATCHES_TABS } from '../../routing/batchesRoutes';
import { ISettlementDetailsData } from '../../store/order/order-interfaces';
import { ExportCSVButton } from '../form/ExportCSVButton';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons';
import { paymentsActionTypes } from '../../store/payments/payments-action-types';
import { useTranslation } from 'react-i18next';

export const getBatchDetailsPageName = (pathname: string, batchId: string): BATCHES_TABS => {
	switch (pathname) {
		case `${BATCHES_BASE_URL}/${BATCHES_TABS.UNPOSTED_BATCHES}/${batchId}`:
			return BATCHES_TABS.UNPOSTED_BATCHES;
		case `${BATCHES_BASE_URL}/${BATCHES_TABS.BATCHES}/${batchId}`:
			return BATCHES_TABS.BATCHES;
		case `${BATCHES_BASE_URL}/${BATCHES_TABS.SHELVED_BATCHES}/${batchId}`:
			return BATCHES_TABS.SHELVED_BATCHES;
	}
};

export const BatchDetails = (): JSX.Element => {
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const { t } = useTranslation();
	const { id: batchDetailsId } = useParams();
	const { pathname, state } = useLocation();
	const navigate = useNavigate();

	const locationPathname = getBatchDetailsPageName(pathname, batchDetailsId);

	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const { paymentContextState, dispatch } = usePaymentContext();
	const {
		unpostedBatchesData,
		batchesData,
		shelvedBatchesData,
	} = paymentContextState;

	const [isDetailsExportLoading, setIsDetailsExportLoading] = useState<boolean>(false);
	const [batchDetailsData, setBatchDetailsData] = useState<ISettlementDetailsData>(null);
	const [isBatchDetailsDataLoading, setIsBatchDetailsDataLoading] = useState<boolean>(true);
	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const fetchDetailsData = async ({ id, params }: IBatchDetailsData): Promise<void> => {
		setIsBatchDetailsDataLoading(true);

		const data = await fetchBatchDetailsData({ id, params });

		setBatchDetailsData(data);

		setIsBatchDetailsDataLoading(false);
	};

	const getBatchDetailsData = (): IBatchesData => {
		switch (locationPathname) {
			case BATCHES_TABS.BATCHES:
				return batchesData?.find(el => el?.payoutId === batchDetailsId);
			case BATCHES_TABS.UNPOSTED_BATCHES:
				return unpostedBatchesData?.find(el => el?.payoutId === batchDetailsId);
			case BATCHES_TABS.SHELVED_BATCHES:
				return shelvedBatchesData?.find(el => el?.payoutId === batchDetailsId);
		}
	};

	const handleExport = async (): Promise<void> => {
		setIsDetailsExportLoading(true);

		await exportCSVBatchDetailsData(batchDetailsId, batchDetailsData?.totalItemCount, locationPathname);

		setIsDetailsExportLoading(false);
	};

	const listener = async (searchParams: IBatchDetailsDataSearchParams): Promise<void> => {
		await fetchDetailsData({ id: batchDetailsId, params: searchParams });
	};

	const handleBack = (): void => {
		dispatch({ type: paymentsActionTypes.SET_UNPOSTED_BATCHES_DATA_LOADING, payload: true });
		dispatch({ type: paymentsActionTypes.SET_BATCHES_DATA_LOADING, payload: true });
		dispatch({ type: paymentsActionTypes.SET_SHELVED_BATCHES_DATA_LOADING, payload: true });

		navigate(state?.search || `${ROUTES.FUNDS}${ROUTES.BATCHES}${ROUTES.UNPOSTED}`);
	};

	return (
		<>
			<PageTitle breadcrumbs={fundsBreadCrumbs}>Reconcile</PageTitle>
			<div className="d-flex align-items-center justify-content-between mb-5">

				<ButtonComponent
					buttonType={BUTTON_TYPE.TRANSPARENT}
					onButtonClick={handleBack}
				>
					<div className="d-flex align-items-center">
						<FontAwesomeIcon icon={faAnglesLeft}/>
						<span className="ms-2">{t('GENERAL.RETURN_BTN_NAME')}</span>
					</div>
				</ButtonComponent>

				{checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
					<OrderStatus statusName={getBatchDetailsData()?.batchType} title="Type"/>
				)}
			</div>

			<BatchDetailsInformation fetchDetailsData={fetchDetailsData} />

			<Table
				name={TablesNames.payoutDetailsTableData}
				data={batchDetailsData?.rows ?? []}
				isLoading={isBatchDetailsDataLoading}
				totalItemCount={batchDetailsData?.totalItemCount ?? 0}
				queryParamsListener={listener}
				toolbarCustomAction={
					<ExportCSVButton
						orderTotalItemCount={batchDetailsData?.totalItemCount ?? 0}
						isExportCSVLoading={isDetailsExportLoading}
						exportCSV={handleExport}
					/>
				}
			/>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
